/**
 * Слой меток интерактивной гиперболической карты: отрисовка иконок элементов
 * легенды, hit-test по клику/наведению и подсветка кликабельности.
 *
 * Метки хранятся в **координатах поверхности** `z` (в канонической системе
 * фундаментальной области, Требование 5.1, 10.3). Отображаемая позиция метки —
 * это точка диска `w = V(γ(z))`, где `V` — текущий вид, а `γ` — одна из соседних
 * копий области (образующая фуксовой группы). Одна метка может появиться в кадре
 * несколько раз через разные копии `γ` (Требование 5.3). Точка `w` проецируется
 * в пиксельные координаты холста тем же преобразованием, что и в рендерере
 * (см. {@link MapViewController.diskToScreen}).
 *
 * Геометрия и hit-test вынесены в чистые функции ({@link computeMarkerScreenPositions},
 * {@link hitTestInstances}), не зависящие от DOM, — они покрываются юнит-тестами.
 * Иконки рисуются как абсолютно позиционированный DOM-оверлей поверх WebGL-холста
 * (проще всего поддерживает изображения иконок легенды и смену курсора при
 * наведении). Оверлей и иконки прозрачны для мыши (`pointer-events: none`), чтобы
 * не мешать перетаскиванию/зуму холста; наведение и клик обрабатываются через
 * hit-test на переданном источнике событий (обычно — сам холст).
 *
 * При наведении на метку курсор источника событий меняется на `pointer`, а иконке
 * добавляется класс подсветки (Требование 5.4). Клик по метке вызывает колбэк
 * `onMarkerClick(marker, legendItem)` — боковая панель с развёрнутым описанием
 * подключается в задаче 16 (Требование 5.2).
 *
 * Требования: 5.1, 5.2, 5.3, 5.4
 */

import type { Complex, Geometry, Isometry, LegendItem, MapMarker, Mobius } from '@langton/shared';
import { asIsometry, hyperbolicGeometry } from '@langton/shared';
import type { ScreenPoint, Viewport } from './map-view-controller.js';

/** CSS-класс контейнера-оверлея меток. */
const OVERLAY_CLASS = 'map-marker-overlay';
/** CSS-класс отдельной иконки метки. */
const MARKER_CLASS = 'map-marker';
/** CSS-класс подсветки метки при наведении (Требование 5.4). */
const MARKER_HOVER_CLASS = 'map-marker--hover';

/** Размер иконки метки по умолчанию (в пикселях). */
const DEFAULT_ICON_SIZE = 32;

/**
 * Отображаемый экземпляр метки: конкретная копия метки в кадре (через соседа `γ`)
 * с вычисленной позицией на экране. Одна метка может дать несколько экземпляров
 * (разные копии через склейку, Требование 5.3).
 */
export interface MarkerScreenInstance {
  /** Исходная метка (координаты поверхности). */
  marker: MapMarker;
  /** Элемент легенды метки, если известен (по `legendItemId`). */
  legendItem?: LegendItem;
  /** Индекс соседней копии `γ` в массиве соседей (0 — тождество/сама область). */
  neighborIndex: number;
  /** Отображаемая точка диска `w = V(γ(z))`. */
  disk: Complex;
  /** Позиция иконки на экране (в пикселях холста/оверлея). */
  screen: ScreenPoint;
}

/**
 * Проекция точки диска в пиксельные координаты. Совместима с сигнатурой
 * {@link MapViewController.diskToScreen}: обычно передаётся как
 * `(w, vp, s) => controller.diskToScreen(w, vp, s)`.
 */
export type ProjectToScreen = (
  w: Complex,
  viewport: Viewport,
  scale: number,
) => ScreenPoint;

/** Параметры вычисления экранных позиций меток ({@link computeMarkerScreenPositions}). */
export interface ComputePositionsParams {
  /** Метки карты (координаты поверхности). */
  markers: MapMarker[];
  /** Текущий вид `V` (матрица или обобщённая изометрия с признаком обращения). */
  view: Mobius | Isometry;
  /** Соседние копии области `γ` (матрицы или изометрии); первый обычно тождество. */
  neighbors: (Mobius | Isometry)[];
  /** Масштаб отображения диска. */
  scale: number;
  /** Размеры холста/оверлея в пикселях. */
  viewport: Viewport;
  /** Проекция точки диска в пиксели (см. {@link ProjectToScreen}). */
  project: ProjectToScreen;
  /** Справочник элементов легенды по идентификатору (для обогащения экземпляров). */
  legendById?: Map<string, LegendItem>;
  /**
   * Запас в пикселях вокруг видимой области, в пределах которого экземпляр всё
   * ещё считается видимым (иконка может частично выходить за край). По умолчанию
   * половина размера иконки.
   */
  cullMargin?: number;
  /**
   * Геометрия карты (Требования 6, 8): её действие используется для вычисления
   * отображаемой точки `V(γ(z))`, честно учитывая антиголоморфные (зеркальные)
   * копии неориентируемой поверхности. По умолчанию гиперболическая — для
   * матриц {@link Mobius} результат совпадает с прежним `apply(view, apply(γ, z))`.
   */
  geometry?: Geometry;
}

/**
 * Вычислить экранные позиции всех видимых экземпляров меток.
 *
 * Для каждой метки `m` с координатой поверхности `z` и каждой соседней копии `γ`
 * отображаемая точка диска — `w = V(γ(z))` = `apply(view, apply(γ, z))`. Точка
 * проецируется в пиксели через `project`. Экземпляр сохраняется, только если его
 * пиксельная позиция попадает в видимую область холста с запасом `cullMargin`
 * (отсечение копий вне кадра, Требование 5.3); прочие отбрасываются.
 *
 * Чистая функция без DOM — пригодна для юнит-тестов.
 *
 * @param params метки, вид, соседи, масштаб, вьюпорт и проекция
 * @returns список видимых экземпляров с экранными позициями
 */
export function computeMarkerScreenPositions(
  params: ComputePositionsParams,
): MarkerScreenInstance[] {
  const {
    markers,
    view,
    neighbors,
    scale,
    viewport,
    project,
    legendById,
    cullMargin = DEFAULT_ICON_SIZE / 2,
    geometry = hyperbolicGeometry,
  } = params;

  const viewIso = asIsometry(view);
  const copies = (neighbors.length > 0 ? neighbors : [identityFallback()]).map(asIsometry);
  const instances: MarkerScreenInstance[] = [];

  for (const marker of markers) {
    const z = marker.surface;
    if (!z || !Number.isFinite(z.re) || !Number.isFinite(z.im)) {
      continue;
    }
    const legendItem = legendById?.get(marker.legendItemId);
    for (let i = 0; i < copies.length; i++) {
      // Отображаемая точка: сначала копия γ(z), затем вид V(γ(z)) — в действии
      // геометрии, с учётом антиголоморфности копии/вида (Требование 8).
      const disk = geometry.apply(viewIso, geometry.apply(copies[i], z));
      if (!Number.isFinite(disk.re) || !Number.isFinite(disk.im)) {
        continue;
      }
      const screen = project(disk, viewport, scale);
      if (!isWithinViewport(screen, viewport, cullMargin)) {
        continue;
      }
      instances.push({ marker, legendItem, neighborIndex: i, disk, screen });
    }
  }

  return instances;
}

/**
 * Найти ближайший экземпляр метки к точке `(x, y)` в пределах радиуса `radius`
 * (порог попадания по иконке). Используется и для клика, и для наведения.
 *
 * Чистая функция без DOM.
 *
 * @param instances экранные экземпляры меток ({@link computeMarkerScreenPositions})
 * @param x координата X в пикселях
 * @param y координата Y в пикселях
 * @param radius радиус попадания в пикселях
 * @returns ближайший экземпляр в пределах радиуса либо `null`
 */
export function hitTestInstances(
  instances: MarkerScreenInstance[],
  x: number,
  y: number,
  radius: number,
): MarkerScreenInstance | null {
  let best: MarkerScreenInstance | null = null;
  let bestDistSq = radius * radius;
  for (const instance of instances) {
    const dx = instance.screen.x - x;
    const dy = instance.screen.y - y;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      best = instance;
    }
  }
  return best;
}

/** Проверить, попадает ли экранная точка в видимую область с запасом `margin`. */
function isWithinViewport(
  screen: ScreenPoint,
  viewport: Viewport,
  margin: number,
): boolean {
  return (
    screen.x >= -margin &&
    screen.x <= viewport.width + margin &&
    screen.y >= -margin &&
    screen.y <= viewport.height + margin
  );
}

/** Тождественная изометрия — запасной сосед, если список пуст. */
function identityFallback(): Mobius {
  return { alpha: { re: 1, im: 0 }, beta: { re: 0, im: 0 } };
}

/** Опции конфигурации {@link MarkerLayer}. */
export interface MarkerLayerOptions {
  /**
   * Колбэк клика по метке: открывает боковую панель с развёрнутым описанием
   * элемента легенды (проводка панели — задача 16, Требование 5.2).
   */
  onMarkerClick?: (marker: MapMarker, legendItem?: LegendItem) => void;
  /**
   * Колбэк смены наведённой метки (или `null`, когда курсор ушёл с меток).
   * Полезен для внешней подсветки/подсказок.
   */
  onMarkerHover?: (instance: MarkerScreenInstance | null) => void;
  /** Проекция точки диска в пиксели (обычно `controller.diskToScreen`). */
  project?: ProjectToScreen;
  /** Размер иконки метки в пикселях (по умолчанию `32`). */
  iconSize?: number;
  /**
   * Радиус попадания по метке в пикселях для hit-test (по умолчанию — половина
   * {@link MarkerLayerOptions.iconSize}).
   */
  hitRadius?: number;
  /**
   * Запас в пикселях для отсечения копий вне кадра (по умолчанию — половина
   * {@link MarkerLayerOptions.iconSize}).
   */
  cullMargin?: number;
}

/** Внутренняя запись: экземпляр метки и его DOM-иконка. */
interface RenderedMarker {
  instance: MarkerScreenInstance;
  element: HTMLElement;
}

/**
 * Слой меток карты: DOM-оверлей иконок поверх холста + hit-test для клика и
 * наведения.
 *
 * Жизненный цикл:
 * 1. `new MarkerLayer(overlay, options)` — оверлей позиционируется поверх холста.
 * 2. `setLegend(items)` / `setMarkers(markers)` — данные легенды и меток.
 * 3. `update(view, scale, viewport, neighbors)` — пересчёт позиций и репозиция иконок.
 * 4. `attach(eventTarget)` — обработчики наведения/клика (обычно на холсте).
 * 5. `detach()` / `dispose()` — снятие обработчиков и очистка DOM.
 */
export class MarkerLayer {
  private readonly overlay: HTMLElement;
  private readonly options: Required<
    Omit<MarkerLayerOptions, 'onMarkerClick' | 'onMarkerHover' | 'project'>
  > & {
    onMarkerClick?: (marker: MapMarker, legendItem?: LegendItem) => void;
    onMarkerHover?: (instance: MarkerScreenInstance | null) => void;
    project?: ProjectToScreen;
  };

  private markers: MapMarker[] = [];
  private legendById = new Map<string, LegendItem>();

  // Последнее переданное состояние вида (для пересчёта при смене данных).
  private view: Mobius | Isometry | null = null;
  private scale = 1;
  private viewport: Viewport = { width: 1, height: 1 };
  private neighbors: (Mobius | Isometry)[] = [];
  /** Геометрия карты для вычисления отображаемых позиций меток (Требования 6, 8). */
  private geometry: Geometry = hyperbolicGeometry;

  /** Текущие отрисованные экземпляры (пул DOM-иконок для переиспользования). */
  private rendered: RenderedMarker[] = [];
  /** Экземпляр под курсором (для подсветки, Требование 5.4). */
  private hovered: MarkerScreenInstance | null = null;

  // Источник событий (обычно холст) и обработчики.
  private eventTarget: HTMLElement | null = null;
  private readonly moveHandler = (e: MouseEvent): void => this.onMouseMove(e);
  private readonly clickHandler = (e: MouseEvent): void => this.onClick(e);
  private readonly leaveHandler = (): void => this.onMouseLeave();

  /**
   * @param overlay DOM-элемент оверлея поверх холста (позиционируется абсолютно)
   * @param options колбэки, проекция и параметры иконок
   */
  constructor(overlay: HTMLElement, options: MarkerLayerOptions = {}) {
    this.overlay = overlay;
    const iconSize = options.iconSize ?? DEFAULT_ICON_SIZE;
    this.options = {
      iconSize,
      hitRadius: options.hitRadius ?? iconSize / 2,
      cullMargin: options.cullMargin ?? iconSize / 2,
      onMarkerClick: options.onMarkerClick,
      onMarkerHover: options.onMarkerHover,
      project: options.project,
    };
    // Оверлей прозрачен для мыши, чтобы не мешать перетаскиванию/зуму холста.
    if (this.overlay && this.overlay.classList) {
      this.overlay.classList.add(OVERLAY_CLASS);
      this.overlay.style.position = this.overlay.style.position || 'absolute';
      this.overlay.style.pointerEvents = 'none';
    }
  }

  /** Задать элементы легенды (для иконок и обогащения экземпляров при клике). */
  setLegend(items: LegendItem[]): void {
    this.legendById = new Map();
    for (const item of items) {
      this.legendById.set(item.id, item);
    }
    if (this.view) {
      this.reposition();
    }
  }

  /** Задать метки карты (координаты поверхности). */
  setMarkers(markers: MapMarker[]): void {
    this.markers = markers.slice();
    if (this.view) {
      this.reposition();
    }
  }

  /**
   * Пересчитать позиции меток под текущий вид и перерисовать иконки
   * (Требования 5.1, 5.3).
   *
   * @param view текущий вид `V`
   * @param scale масштаб отображения диска
   * @param viewport размеры холста/оверлея в пикселях
   * @param neighbors соседние копии области (изометрии `γ`)
   */
  update(
    view: Mobius | Isometry,
    scale: number,
    viewport: Viewport,
    neighbors: (Mobius | Isometry)[],
    geometry?: Geometry,
  ): void {
    this.view = view;
    this.scale = scale;
    this.viewport = viewport;
    this.neighbors = neighbors;
    if (geometry) {
      this.geometry = geometry;
    }
    this.reposition();
  }

  /**
   * Вычислить видимые экземпляры меток для текущего состояния (без изменения DOM).
   * Возвращает пустой массив, если проекция не задана или вид не установлен.
   */
  computeInstances(): MarkerScreenInstance[] {
    if (!this.view || !this.options.project) {
      return [];
    }
    return computeMarkerScreenPositions({
      markers: this.markers,
      view: this.view,
      neighbors: this.neighbors,
      scale: this.scale,
      viewport: this.viewport,
      project: this.options.project,
      legendById: this.legendById,
      cullMargin: this.options.cullMargin,
      geometry: this.geometry,
    });
  }

  /**
   * Hit-test экранной точки против текущих видимых экземпляров.
   *
   * @param screenX координата X в пикселях
   * @param screenY координата Y в пикселях
   * @returns ближайший экземпляр в пределах радиуса иконки либо `null`
   */
  hitTest(screenX: number, screenY: number): MarkerScreenInstance | null {
    return hitTestInstances(
      this.rendered.map((r) => r.instance),
      screenX,
      screenY,
      this.options.hitRadius,
    );
  }

  /**
   * Подключить обработчики наведения/клика к источнику событий (обычно холсту).
   * Наведение меняет курсор на `pointer` и подсвечивает иконку (Требование 5.4);
   * клик по метке вызывает `onMarkerClick` (Требование 5.2). Безопасно
   * игнорируется в не-браузерном окружении.
   *
   * @param eventTarget элемент-источник событий мыши (холст карты)
   */
  attach(eventTarget: HTMLElement): void {
    if (typeof window === 'undefined' || !eventTarget) {
      return;
    }
    this.detach();
    this.eventTarget = eventTarget;
    eventTarget.addEventListener('mousemove', this.moveHandler);
    eventTarget.addEventListener('click', this.clickHandler);
    eventTarget.addEventListener('mouseleave', this.leaveHandler);
  }

  /** Снять обработчики мыши и сбросить состояние наведения. */
  detach(): void {
    if (this.eventTarget) {
      this.eventTarget.removeEventListener('mousemove', this.moveHandler);
      this.eventTarget.removeEventListener('click', this.clickHandler);
      this.eventTarget.removeEventListener('mouseleave', this.leaveHandler);
      this.clearHover();
    }
    this.eventTarget = null;
  }

  /** Снять обработчики и удалить все DOM-иконки. */
  dispose(): void {
    this.detach();
    for (const r of this.rendered) {
      r.element.remove();
    }
    this.rendered = [];
    this.markers = [];
    this.legendById.clear();
  }

  /** Пересчитать экземпляры и синхронизировать DOM-иконки. */
  private reposition(): void {
    const instances = this.computeInstances();
    this.syncDom(instances);
  }

  /**
   * Синхронизировать пул DOM-иконок с текущим списком экземпляров: переиспользует
   * элементы, создаёт недостающие, удаляет лишние.
   */
  private syncDom(instances: MarkerScreenInstance[]): void {
    if (typeof document === 'undefined') {
      // Не-браузерное окружение: сохраняем экземпляры без DOM (для hit-test/тестов).
      this.rendered = instances.map((instance) => ({
        instance,
        element: null as unknown as HTMLElement,
      }));
      return;
    }

    // Удалить лишние элементы, если меток стало меньше.
    while (this.rendered.length > instances.length) {
      const extra = this.rendered.pop();
      extra?.element.remove();
    }

    for (let i = 0; i < instances.length; i++) {
      const instance = instances[i];
      let record = this.rendered[i];
      if (!record) {
        const element = this.createMarkerElement();
        this.overlay.appendChild(element);
        record = { instance, element };
        this.rendered[i] = record;
      } else {
        record.instance = instance;
      }
      this.applyMarkerElement(record.element, instance);
    }

    // Заново применить подсветку к экземпляру под курсором, если он ещё виден.
    this.refreshHoverHighlight();
  }

  /** Создать DOM-элемент иконки метки. */
  private createMarkerElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = MARKER_CLASS;
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.width = `${this.options.iconSize}px`;
    el.style.height = `${this.options.iconSize}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundPosition = 'center';
    // Центрируем иконку относительно вычисленной точки.
    el.style.transform = 'translate(-50%, -50%)';
    return el;
  }

  /** Применить позицию, иконку и подпись экземпляра к DOM-элементу. */
  private applyMarkerElement(el: HTMLElement, instance: MarkerScreenInstance): void {
    el.style.left = `${instance.screen.x}px`;
    el.style.top = `${instance.screen.y}px`;
    const legend = instance.legendItem;
    if (legend?.iconUrl) {
      el.style.backgroundImage = `url("${legend.iconUrl}")`;
      el.style.backgroundColor = '';
    } else {
      // Без иконки — простой цветной маркер-точка.
      el.style.backgroundImage = '';
      el.style.backgroundColor = 'rgba(77, 163, 255, 0.85)';
      el.style.borderRadius = '50%';
    }
    if (legend?.name) {
      el.title = legend.name;
      el.setAttribute('aria-label', legend.name);
    }
    el.dataset.markerId = instance.marker.id;
    el.dataset.legendItemId = instance.marker.legendItemId;
  }

  /** Обработчик движения мыши: подсветка/курсор при наведении (Требование 5.4). */
  private onMouseMove(e: MouseEvent): void {
    const point = this.eventPoint(e);
    const hit = this.hitTest(point.x, point.y);
    if (hit === this.hovered) {
      return;
    }
    this.setHover(hit);
  }

  /** Обработчик ухода курсора с источника событий: снять подсветку. */
  private onMouseLeave(): void {
    this.setHover(null);
  }

  /** Обработчик клика: открыть панель для метки под курсором (Требование 5.2). */
  private onClick(e: MouseEvent): void {
    const point = this.eventPoint(e);
    const hit = this.hitTest(point.x, point.y);
    if (hit) {
      this.options.onMarkerClick?.(hit.marker, hit.legendItem);
    }
  }

  /** Обновить состояние наведения: курсор, класс подсветки и колбэк. */
  private setHover(hit: MarkerScreenInstance | null): void {
    this.hovered = hit;
    if (this.eventTarget) {
      this.eventTarget.style.cursor = hit ? 'pointer' : '';
    }
    this.refreshHoverHighlight();
    this.options.onMarkerHover?.(hit);
  }

  /** Сбросить состояние наведения без вызова колбэка (при detach). */
  private clearHover(): void {
    this.hovered = null;
    if (this.eventTarget) {
      this.eventTarget.style.cursor = '';
    }
    this.refreshHoverHighlight();
  }

  /** Применить класс подсветки к элементу наведённого экземпляра. */
  private refreshHoverHighlight(): void {
    const hoveredMarkerId = this.hovered?.marker.id;
    const hoveredIndex = this.hovered?.neighborIndex;
    for (const record of this.rendered) {
      if (!record.element || !record.element.classList) {
        continue;
      }
      const isHovered =
        this.hovered !== null &&
        record.instance.marker.id === hoveredMarkerId &&
        record.instance.neighborIndex === hoveredIndex;
      record.element.classList.toggle(MARKER_HOVER_CLASS, isHovered);
    }
  }

  /** Пиксельные координаты события мыши относительно источника событий. */
  private eventPoint(e: MouseEvent): ScreenPoint {
    const target = this.eventTarget;
    if (target && typeof target.getBoundingClientRect === 'function') {
      const rect = target.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    return { x: e.clientX, y: e.clientY };
  }
}
